import type { NombreIcono } from '../../componentes/iconos';
import type { TarjetaAyuda } from '../../marco/cabecera';

/** Textos de «Servicios y profesionales» (9.4 a 9.7 y 9.11), tal cual el diseño (`HELP` del prototipo). */
const t = (icono: NombreIcono, titulo: string, texto: string): TarjetaAyuda => ({ icono, titulo, texto });

export const AYUDA = {
    servicios: [
        t('scissors', 'Tu catálogo', 'Cada servicio con su duración, su precio y quién lo hace. Es el mismo en la web, el widget, el chat y el panel.'),
        t('history', 'Versiones', 'Cambiar un servicio crea una versión nueva: las citas ya hechas conservan su duración y su precio.'),
        t('upload', 'Importar', 'Si vienes de otro programa, sube tu catálogo en un CSV con la plantilla. Antes de importar eliges qué entra.'),
    ],
    profesionales: [
        t('contact', 'Tu equipo', 'Quién atiende. Recibe citas aunque no entre al panel: su agenda la lleváis vosotros.'),
        t('user-plus', 'Profesional extra', 'Tu plan incluye un profesional activo; cada profesional extra suma uno más.'),
        t('user-x', 'Desactivar', 'Nunca se borra: deja de recibir citas y de contar para tu plan. Antes ves qué pasa con sus citas.'),
    ],
    horarios: [
        t('clock', 'Su semana', 'Cada profesional usa el horario del negocio o uno propio. Los festivos y cierres del negocio le cierran siempre.'),
        t('calendar-x', 'Días especiales', 'Vacaciones, médico o formación: solo para ese profesional.'),
        t('calendar-clock', 'Citas afectadas', 'Si un cambio deja citas fuera, antes de guardar ves cuáles. Después decides con cada cliente: otra hora, otro profesional o cancelar.'),
    ],
    consentimientos: [
        t('file-text', 'Plantillas', 'El texto que firma el cliente. Cada servicio elige la suya en su ficha.'),
        t('signature', 'Firma', 'Se firma desde el móvil con un proveedor de firma eIDAS, o en la tablet del centro si no se firmó antes.'),
        t('history', 'Versiones', 'Cambiar el texto publica una versión nueva. Lo ya firmado conserva la suya.'),
    ],
    nuevo: [
        t('scissors', 'Nuevo servicio', 'Basta con el nombre, la duración y el precio. Las preguntas se añaden después, en su ficha.'),
        t('contact', 'Quién lo hace', 'Están marcados todos los profesionales activos. Sin nadie que lo haga, no se puede pedir.'),
    ],
    datos: [
        t('scissors', 'Datos', 'Guardar crea una versión nueva. Las citas ya hechas conservan la suya.'),
        t('credit-card', 'Política de cobro', 'La señal o el prepago se asignan en Pagos › Políticas, igual para todos los canales.'),
        t('signature', 'Consentimiento', 'Si lo eliges, al pedir la cita se le envía al cliente para firmar.'),
    ],
    preguntas: [
        t('list-checks', 'Preguntas', 'Ajustan la duración y el precio según la respuesta: el largo del pelo, cuántas mechas…'),
        t('archive', 'Retirar', 'Una pregunta retirada deja de preguntarse, pero las citas que la respondieron la conservan.'),
    ],
    quien: [t('contact', 'Quién lo hace', 'Solo se ofrecen huecos de los profesionales activos que lo hacen.')],
    versiones: [t('history', 'Versiones', 'Cada cambio es una versión. Una cita muestra siempre la versión con la que se pidió.')],
    importar: [
        t('file-spreadsheet', 'La plantilla', 'Servicios con su duración, su precio y sus variantes, y profesionales con los servicios que hacen.'),
        t('list-checks', 'Elegir', 'Antes de importar ves qué se crea y qué ya existe. Lo que ya tienes no se duplica.'),
        t('user-x', 'Profesionales', 'Llegan desactivados y sin acceso: los activas tú, según tu plan.'),
    ],
    proNew: [
        t('user-plus', 'Alta', 'Su nombre y los servicios que hace. Su teléfono y su email son para avisarle de sus citas.'),
        t('key-round', 'Acceso al panel', 'Si lo tendrá, le llega una invitación con rol Profesional. Su contraseña la elige al aceptarla.'),
        t('contact', 'Tu plan', 'Si no cabe en tu plan, se guarda desactivado hasta que contrates un profesional extra.'),
    ],
    proDatos: [
        t('user', 'Datos', 'Su nombre y su foto se ven en tu web al elegir profesional.'),
        t('user-x', 'Desactivar', 'Antes ves sus citas futuras y eliges si se recolocan o se cancelan.'),
    ],
    proSvcs: [t('scissors', 'Servicios', 'Los que puede hacer. Si un servicio se queda sin nadie, deja de poder pedirse.')],
    acceso: [
        t('key-round', 'Acceso', 'Con acceso ve su agenda y sus citas en el panel. Sin acceso también recibe citas.'),
        t('user-cog', 'Equipo y roles', 'La invitación se reenvía o se anula, y el acceso se retira, en Equipo y roles.'),
    ],
    horario: [t('clock', 'Su horario', 'Se cambia en el apartado Horarios, con sus días especiales.')],
    exc: [
        t('calendar-x', 'Día especial', 'No trabaja o trabaja con otro horario solo ese día o esos días.'),
        t('calendar-clock', 'Sus citas', 'Si deja citas fuera, antes de guardar ves cuáles.'),
    ],
    tpl: [
        t('file-text', 'Texto', 'Escribe lo que firma el cliente. Los datos de la cita se rellenan solos.'),
        t('history', 'Versiones', 'Publicar crea una versión nueva; lo firmado conserva la suya.'),
    ],
    tplPrev: [t('eye', 'Vista previa', 'Con datos de ejemplo, como lo verá el cliente en su móvil.')],
} as const;

export type Apartado = 'servicios' | 'profesionales' | 'horarios' | 'consentimientos';

export const APARTADOS: readonly { clave: Apartado; texto: string; corta: string; icono: NombreIcono }[] = [
    { clave: 'servicios', texto: 'Servicios', corta: 'Servicios', icono: 'scissors' },
    { clave: 'profesionales', texto: 'Profesionales', corta: 'Equipo', icono: 'contact' },
    { clave: 'horarios', texto: 'Horarios', corta: 'Horarios', icono: 'clock' },
    { clave: 'consentimientos', texto: 'Consentimientos', corta: 'Firmas', icono: 'signature' },
];

export type PestanaServicio = 'datos' | 'preguntas' | 'quien' | 'versiones';

export const PESTANAS: readonly [PestanaServicio, string, NombreIcono][] = [
    ['datos', 'Datos', 'scissors'],
    ['preguntas', 'Preguntas', 'list-checks'],
    ['quien', 'Quién lo hace', 'contact'],
    ['versiones', 'Versiones', 'history'],
];

export const esPestana = (x: string | null): x is PestanaServicio => x === 'datos' || x === 'preguntas' || x === 'quien' || x === 'versiones';

export const PERMISOS = {
    ver: 'citas.agenda.ver',
    /** Quien entra en «Servicios y profesionales» (G779): cualquiera de los tres. */
    entrar: ['citas.catalogo.gestionar', 'citas.profesionales.gestionar', 'disponibilidad.cambios.confirmar'],
    gestionar: 'citas.catalogo.gestionar',
    importar: 'citas.catalogo.importar',
    profesionales: 'citas.profesionales.gestionar',
    horarios: 'disponibilidad.cambios.confirmar',
    plantillas: 'documentos.plantillas.gestionar',
    ajustes: 'citas.ajustes.gestionar',
    recolocar: 'citas.citas.gestionar',
    verHorarios: 'disponibilidad.horarios.ver',
    gestionarHorarios: 'disponibilidad.horarios.gestionar',
    politicas: 'pagos.politicas.gestionar',
} as const;

export const SOLO_VER = 'Con tu rol puedes consultarlo, pero no cambiarlo.';
export const SOLO_LECTURA = 'El negocio está en solo lectura: puedes consultar, pero no cambiar nada.';
